import { CustomWidgetCollection, Question, Serializer } from 'survey-core';
import type { EventMaterialPlannerHostComponent } from '../components/dynamic-form/event-material-planner-host/event-material-planner-host.component';

class EventMaterialPlannerQuestion extends Question {
  override getType() {
    return 'eventmaterialplanner';
  }
}

Serializer.addClass(
  'eventmaterialplanner',
  [],
  () => new EventMaterialPlannerQuestion('eventmaterialplanner'),
  'question'
);

type HostRef = {
  instance: EventMaterialPlannerHostComponent;
  hostView: unknown;
  changeDetectorRef: { detectChanges: () => void };
  destroy: () => void;
};

type PlannerQuestionState = Question & {
  _materialHostRef?: HostRef;
  _materialContainer?: HTMLElement;
  _materialMountPromise?: Promise<void>;
};

export function teardownEventMaterialPlanner(
  question: Question | null | undefined
): void {
  if (!question || question.getType() !== 'eventmaterialplanner') {
    return;
  }
  destroyMaterialHost(question);
}

function destroyMaterialHost(question: Question): void {
  const componentRef = (question as PlannerQuestionState)._materialHostRef;
  const container = (question as PlannerQuestionState)._materialContainer;
  if (container) {
    container.replaceChildren();
  }
  if (!componentRef) {
    return;
  }
  const appRef = (
    window as { surveyAppRef?: { detachView: (v: unknown) => void } }
  ).surveyAppRef;
  try {
    appRef?.detachView(componentRef.hostView);
    componentRef.destroy();
  } catch {
    /* ignore */
  }
  delete (question as PlannerQuestionState)._materialHostRef;
  delete (question as PlannerQuestionState)._materialContainer;
}

async function mountMaterialHost(
  question: Question,
  element: HTMLElement
): Promise<void> {
  const root =
    element.querySelector('.event-material-planner-root') ?? element;
  const container = root as HTMLElement;
  const q = question as PlannerQuestionState;

  if (q._materialHostRef?.instance && q._materialContainer === container) {
    return;
  }

  if (q._materialHostRef) {
    destroyMaterialHost(question);
  }

  if (q._materialMountPromise) {
    await q._materialMountPromise;
    return;
  }

  const mountWork = async (): Promise<void> => {
    const injector = (window as { surveyInjector?: unknown }).surveyInjector;
    const appRef = (
      window as {
        surveyAppRef?: {
          attachView: (v: unknown) => void;
          detachView: (v: unknown) => void;
        };
      }
    ).surveyAppRef;
    const environmentInjector = (window as { surveyEnvInjector?: unknown })
      .surveyEnvInjector;

    if (!injector || !appRef || !environmentInjector) {
      container.textContent = 'Kunne ikke indlæse materielplanlægger.';
      return;
    }

    const { EventMaterialPlannerHostComponent } = await import(
      '../components/dynamic-form/event-material-planner-host/event-material-planner-host.component'
    );
    const { createComponent } = await import('@angular/core');

    container.replaceChildren();

    const componentRef = createComponent(EventMaterialPlannerHostComponent, {
      environmentInjector: environmentInjector as never,
      hostElement: container,
      elementInjector: injector as never,
    }) as HostRef;

    componentRef.instance.question = question;
    q._materialContainer = container;
    appRef.attachView(componentRef.hostView);
    componentRef.changeDetectorRef.detectChanges();
    q._materialHostRef = componentRef;
  };

  q._materialMountPromise = mountWork();
  try {
    await q._materialMountPromise;
  } finally {
    delete q._materialMountPromise;
  }
}

export const eventMaterialPlannerWidget = {
  name: 'eventmaterialplanner',
  title: 'Event material planner',
  iconName: 'icon-toolbox',
  category: 'Custom Questions',
  widgetIsLoaded: () => true,
  isFit: (question: Question) => question.getType() === 'eventmaterialplanner',
  activatedByChanged: (activatedBy: string) => activatedBy === 'property',
  isDefaultRender: false,
  htmlTemplate: "<div class='event-material-planner-root'></div>",
  afterRender: async (question: Question, element: HTMLElement) => {
    await mountMaterialHost(question, element);
  },
  destroy: (question: Question) => {
    destroyMaterialHost(question);
  },
};

CustomWidgetCollection.Instance.add(eventMaterialPlannerWidget, 'customtype');
